import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../assets/style/_global.scss';
import { Typography, Toolbar, Card, CardActionArea, CardMedia, CardContent, Grid, makeStyles, CircularProgress, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { Project, Agent, CSProjectSettings } from '@moderepo/mode-internal-apis';
import { fetchAgentProjects, ritzDataContext, selectLoggedInAgent, selectProjects } from '@moderepo/mode-data-state-ritz';
import { SearchBox, capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { RouteHelper } from '..';
import { useRitzTranslation } from '../../utils/customHooks';
import projectBanner from '../../assets/images/project_banner.png';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            '&.title-bar': {
                flexDirection  : 'column',
                '& .title-text': {
                    width: '100%',
                },
                [theme.breakpoints.up('sm')]: {
                    flexDirection: 'row',
                },
            },
        },

        pageTitle: {
            whiteSpace                  : 'nowrap',
            width                       : '100%',
            paddingBottom               : theme.spacing(2),
            [theme.breakpoints.up('sm')]: {
                paddingBottom: theme.spacing(0),
                paddingRight : theme.spacing(3),
                textAlign    : 'left',
            },
        },


        searchBoxContainer: {
            position                    : 'relative',
            marginLeft                  : 'auto',
            width                       : '100%',
            [theme.breakpoints.up('sm')]: {
                width   : 'auto',
                minWidth: '15em',
                maxWidth: '20em',
            },
        },


        projectList: {
            margin  : '0 auto',
            maxWidth: 2500,
        },

        projectCard: {
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',

            '&.disabled': {
                pointerEvents: 'none',
                filter       : 'grayScale(1)',
                opacity      : 0.5,
            },
        },

        projectCardActionArea: {
            height: '100%',
        },

        projectCardBanner: {
            height: '150px',
        },

        projectCardContent: {
            height: '100%',
        },

        projectId: {
            fontWeight : 'bold',
            marginRight: theme.spacing(1),
        },

        projectName: {

        },

        projectCardActions: {
            marginTop: 'auto',
        },
    };
}, { name: 'ProjectsListingPage' });



const defaultProjectSettings = {
    banner: projectBanner,
    logo  : '',
};


/**
 * This is the page where we show the agent the list of projects he have access to so that we can pick a
 * project and handle user support for that project.
 */
export const ProjectsListingPage: React.FC = () => {
    const navigate = useNavigate();

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const projects = selectProjects(dataState);
    const { trans } = useRitzTranslation(undefined);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const routeHelper = RouteHelper.getInstance(navigate);
    const classes = useStyle();
    const [searchText, setSearchText] = useState<string>('');

    const [filteredProjects, setFilteredProjects] = useState<readonly Project[] | undefined>(projects);


    /**
     * This useEffect is used for loading all projects which needs to be ran once each time the agent go back to the home page
     */
    useEffect(() => {
        if (loggedInAgent && !projects) {
            dataDispatch(fetchAgentProjects(loggedInAgent.id)).catch(() => { /* Catch to prevent Uncaught error but don't need to do anything */ });
        }
    }, [dataDispatch, loggedInAgent, projects]);


    /**
     * This useEffect is used for filtering projects when the agent change the search input.
     */
    useEffect(() => {
        if (searchText.length > 0 && projects && projects.length > 0) {
            setFilteredProjects(projects.filter((proj: Project): boolean => {
                // Use the project's id, name and description to search
                return proj.id.toString().indexOf(searchText) >= 0
                       || proj.name.toLowerCase().indexOf(searchText) >= 0
                       || proj.description.toLowerCase().indexOf(searchText) >= 0;
            }));
        } else {
            setFilteredProjects(projects);
        }
    }, [projects, searchText]);


    // Wait for projects to be loaded and check if the user has exactly 1 project. If yes, take the agent to the Project dashboard directly.
    useEffect(() => {
        if (projects && projects.length === 1) {
            // If the agent has EXACTLY 1 project, automatically take the agent to that project
            routeHelper.gotoProjectHomePage(projects[0].id);
        }
    }, [projects, routeHelper]);


    /**
     * On search input changed event from SearchBox
     */
    const onSearchTextChange = useCallback((newSearchText: string) => {
        setSearchText(newSearchText);
    }, []);


    /**
     * On project card clicked, take the agent to the Project details page
     */
    const onProjectClicked = useCallback((selectedProject: Project) => {
        routeHelper.gotoProjectHomePage(selectedProject.id);
    }, [routeHelper]);


    /**
     * Create the UI for 1 project
     */
    const createProjectCard = useCallback((selectedProject: Project): JSX.Element => {
        const settings: CSProjectSettings = {
            ...defaultProjectSettings,
            ...selectedProject.supportToolSettings,
        };

        return (
            <Grid key={selectedProject.id} item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Card className={clsx(classes.projectCard, !selectedProject.enableSupportTool && 'disabled')}>
                    <CardActionArea
                        className={classes.projectCardActionArea}
                        onClick={() => {
                            onProjectClicked(selectedProject);
                        }}
                    >
                        <CardMedia
                            className={classes.projectCardBanner}
                            image={settings.banner}
                        />
                        <CardContent className={classes.projectCardContent}>
                            <Typography gutterBottom variant="h5" component="h2">
                                <span className={classes.projectId}>{selectedProject.id}</span>
                                <span className={classes.projectName}>{selectedProject.name}</span>
                            </Typography>
                            <Typography variant="body2" color="textSecondary" component="p">{selectedProject.description}</Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
            </Grid>
        );
    }, [classes, onProjectClicked]);



    return (
        <div className="page-main-content">
            {projects && projects.length > 0 && (
                <>
                    <Toolbar className={clsx(classes.root, 'title-bar')}>
                        <div className="title-bar-item title-text">
                            <div className="page-title">{trans('page.title.my_projects')}</div>
                            <div className="page-subtitle">{trans('page.subtitle.my_projects')}</div>
                        </div>

                        <div className={classes.searchBoxContainer}>
                            <SearchBox
                                placeholder={capitalizeAll(trans('label.search'))}
                                onChange={onSearchTextChange}
                            />
                        </div>
                    </Toolbar>
                    <div className={classes.projectList}>
                        {filteredProjects && filteredProjects.length > 0 ? (
                            <Grid container spacing={3}>
                                {
                                    filteredProjects && filteredProjects.map((proj: Project) => {
                                        return createProjectCard(proj);
                                    })
                                }
                            </Grid>
                        ) : (
                            <GenericMessageBox
                                title={trans('generic.no_data_found.title', {
                                    term: capitalizeAll(trans('label.project', { count: 2 })),
                                })}
                            />
                        )}
                    </div>
                </>
            )}

            {projects && projects.length === 0 && (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.project', { count: 2 })),
                    })}
                />
            )}

            {projects === undefined && (
                <CircularProgress />
            )}
        </div>
    );
};
